import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { API, PositionDTO } from 'src/app/services/api/api';
import { SbDateTime } from 'src/app/services/api/api-date-time';
import { ErrorService } from 'src/app/services/error.service';
import { UtilService } from 'src/app/services/util.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LineString, Point } from 'geojson';

@Component({
  selector: 'sb-device-positions',
  templateUrl: './sb-device-positions.component.html',
  styleUrls: ['./sb-device-positions.component.scss'],
  standalone: false,
})
export class SbDevicePositionsComponent extends DataSource<PositionDTO | undefined> implements OnInit {
  @Input()
  deviceId: number;

  placeholderHeight = 0;
  displayedColumns: string[] = ['timestamp', 'position'];

  private cachedPositions = Array.from<PositionDTO>({ length: 0 });
  private dataStream = new BehaviorSubject<(PositionDTO | undefined)[]>(this.cachedPositions);
  private subscription = new Subscription();

  private pageSize = 10;
  private lastPage = 0;

  loading: boolean = true;

  requesting: boolean = false;

  center: Point;
  points: Point[] = [];
  lineStrings: LineString[] = [];

  constructor(
    private api: API,
    public util: UtilService,
    public errorHandler: ErrorService,
    private destroyRef: DestroyRef
  ) {
    super();
  }

  ngOnInit(): void {
    this._fetchPage(null);
  }

  placeholderWhen(index: number, _: any) {
    return index === 0;
  }

  clear() {
    this.lastPage = 0;
    this.cachedPositions = Array.from<PositionDTO>({ length: 0 });
    this.dataStream.next(this.cachedPositions);
  }

  connect(collectionViewer: CollectionViewer): Observable<(PositionDTO | undefined)[] | ReadonlyArray<PositionDTO | undefined>> {
    return this.dataStream;
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.subscription.unsubscribe();
  }

  private _fetchPage(rangeEnd: number): void {
    if (!this.requesting) {
      this.requesting = true;
      let afterTS: SbDateTime;

      if (rangeEnd && this.cachedPositions && this.cachedPositions.length > 0) {
        const playout: PositionDTO = this.cachedPositions[this.cachedPositions.length - 1];
        afterTS = playout.timestamp;
      } else {
        afterTS = null;
      }

      this.api
        .ss_nm_Device_positions(this.deviceId, {
          after: afterTS,
          count: this.pageSize,
        })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.requesting = false;
          })
        )
        .subscribe({
          next: (res: PositionDTO[]) => {
            this.cachedPositions = this.cachedPositions.concat(res);
            this.dataStream.next(this.cachedPositions);
            if (!res?.length || res?.length === 0 || res?.length < this.pageSize) {
              this.loading = false;
            }
            this.processPositions(this.cachedPositions);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  private _getPageForIndex(i: number): number {
    return Math.floor(i / this.pageSize);
  }

  onTableScroll(e: { target: { offsetHeight: any; scrollHeight: any; scrollTop: any } }) {
    const tableViewHeight = e.target.offsetHeight; // viewport: ~500px
    const tableScrollHeight = e.target.scrollHeight; // length of all table
    const scrollLocation = e.target.scrollTop; // how far user scrolled

    // If the user has scrolled within 200px of the bottom, add more data
    const buffer = 1;
    const limit = tableScrollHeight - tableViewHeight - buffer;
    if (scrollLocation > limit) {
      this.loading = true;
      this._fetchPage(1337);
    }
  }

  processPositions(positions: PositionDTO[]) {
    const maxDiff = 60 * 1000; // 60 seconds

    let currentRoute = null;
    const routes = [];
    let lastPosition = null;
    for (const position of positions) {
      if (lastPosition) {
        const timeDiff = lastPosition.timestamp.value.toMillis() - position.timestamp.value.toMillis();
        if (timeDiff > maxDiff) {
          currentRoute = null;
        }
      }

      if (!currentRoute) {
        currentRoute = [];
        routes.push(currentRoute);
      }
      currentRoute.push(position);

      lastPosition = position;
    }

    const routesAndPoints = this.toMapElements(routes);
    this.lineStrings = routesAndPoints.routes;
    this.points = routesAndPoints.points;
  }

  toMapElements(data: PositionDTO[][]) {
    const obj: { routes: LineString[]; points: Point[] } = { routes: [], points: [] };

    for (const route of data) {
      if (route.length === 0) {
        // YOU GIVE ME NOTHING
      } else if (route.length === 1) {
        obj.points.push(route[0].coordinate);
      } else {
        obj.points.push(route[0].coordinate);
        obj.points.push(route[route.length - 1].coordinate);
        const tempCoordinates = [];
        for (const position of route) {
          tempCoordinates.push(position.coordinate.coordinates);
        }
        const lineString: LineString = { type: 'LineString', coordinates: tempCoordinates };
        obj.routes.push(lineString);
      }
    }

    return obj;
  }
}
